import { z } from "zod";

// http(s)-only, length-bounded URL. The regex emits a JSON Schema `pattern` (server-enforced by
// NJsonSchema — unlike `format: uri`, which is annotation-only), so `javascript:`/`data:`/`file:`
// schemes are rejected at the API boundary before they can be stored and later rendered in chat cards
// (codex #1). max(2000) matches the VARCHAR(2000) columns (codex #3).
const httpUrl = z
  .string()
  .regex(/^https?:\/\/.+/i, "Must be a valid http(s) URL")
  .max(2000);

// Multi-source product create. `brand_id` only — the server derives `source_id` from the chosen
// brand (hierarchy-integrity: a product's source can never disagree with its brand's). jsonb fields
// (`specs`/`metadata`) are sent as JSON strings and parsed to jsonb at the controller boundary.
// String/number bounds mirror the column widths (VARCHAR(50/100/200/1000/2000), DECIMAL(12,2);
// products.name was widened to 1000 by V021).
export const createProductRequestSchema = z.object({
  brand_id: z.number().int(),
  external_id: z.string().min(1, "External ID is required").max(100),
  // UPPERCASE ISO-3166 alpha-2 (or the 'ZZ' non-geo sentinel): the search path compares the
  // request/session country (uppercased) directly to products.country_code, so a lowercase row
  // would be a distinct market key invisible to searches.
  country_code: z.string().regex(/^[A-Z]{2}$/, "Country code must be two uppercase letters (ISO-3166 alpha-2)"),
  // BCP 47 language tag (e.g. en-CA, fr-CA), matching Accept-Language. Canonical casing (lowercase
  // language, uppercase region); a stricter pattern is a follow-on's concern. max(35) matches the column.
  // Required + non-blank (V009: products.language is NOT NULL + CHECK btrim(language) <> '') — the importer
  // always sets it, and an admin create must supply a real (non-whitespace) tag so no product is ever created
  // without a language. The \S pattern (JSON-Schema-representable) rejects a whitespace-only value server-side.
  language: z.string().min(1, "Language is required").max(35).regex(/\S/, "Language cannot be blank"),
  name: z.string().min(1, "Name is required").max(1000),
  // Consumer-facing brand, independent of the source-derived brand_name (StockX source identity).
  manufacturer: z.string().max(200).nullable().optional(),
  category: z.string().max(200).nullable().optional(),
  description: z.string().nullable().optional(),
  price: z.number().min(0, "Price must be zero or greater").max(9999999999.99).nullable().optional(),
  currency: z.string().regex(/^[A-Z]{3}$/, "Currency must be a three-letter ISO-4217 code").nullable().optional(),
  original_price: z.number().min(0, "Original price must be zero or greater").max(9999999999.99).nullable().optional(),
  url: httpUrl,
  tracking_url: httpUrl.nullable().optional(),
  image_url: httpUrl.nullable().optional(),
  rating: z.number().min(0).max(5, "Rating must be between 0 and 5").nullable().optional(),
  rating_count: z.number().int().min(0, "Rating count must be zero or greater").nullable().optional(),
  in_stock: z.boolean().optional(),
  active: z.boolean().optional(),
  specs: z.string().nullable().optional(),
  metadata: z.string().nullable().optional(),
});
